import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, App, Button, Collapse, Input, Radio, Select, Tag, Typography } from 'antd';
import { CopyOutlined } from '@ant-design/icons';

import ContextBar from '../components/common/ContextBar';
import GroupHeader from '../components/common/GroupHeader';
import ToggleRow from '../components/common/ToggleRow';
import { apiErrorMessage } from '../utils/apiErrors';
import { copyTextToClipboard } from '../utils/clipboard';
import { normalizeId } from '../utils/ids';
import { nestSubCollections, nestedOptionClass } from '../utils/collectionFamily';
import { clientUpdateHref } from '../utils/agentClients';
import { listGrants } from '../services/oauth';
import {
	CARDS_SCOPE_LABEL,
	CARDS_SCOPE_NOTE,
	DELETE_SCOPE_LABEL,
	DELETE_SCOPE_NEEDS_WRITE,
	DELETE_SCOPE_NOTE,
	EXPIRY_OPTIONS,
	ONE_TIME_TOKEN_NOTE,
	createApiKey,
	deleteScopeAllowed,
	listApiKeys,
} from '../services/apiKeys';
import {
	CLAUDE_CODE_COMMANDS,
	CLAUDE_DIRECTORY_URL,
	LOGIN_COMMAND,
	LOGIN_KEY_COMMAND,
	MCP_CONNECTOR_URL,
	PLUGIN_MARKETPLACE,
	SKILL_INSTALL_SENTENCE,
	UPDATE_GUIDE_PATH,
} from '../config/agentAccess';
import { useCollections } from '../contexts/CollectionsContext';
import { EXTERNAL_LINK_PROPS } from '../components/common/ExternalLink';

const { Text } = Typography;

/**
 * The three routes (the owner's assistant-channels ruling, d08–d12): where a
 * person uses their assistant, not which piece of software they install.
 * Every one of them signs in on keepr's own consent page — no key to copy,
 * nothing to download — and step 4 turns green when the new connection
 * (an OAuth grant) appears. Claude leads and is the one marked Recommended.
 * `channel` is the route's section in the update guide (utils/agentClients.js).
 */
export const ROUTES = [
	{
		key: 'claude',
		title: 'Claude',
		description: 'Chat on the web, desktop and phone, Claude Code and Cowork.',
		recommended: true,
		channel: 'plugin',
	},
	{
		key: 'apps',
		title: 'ChatGPT and other apps',
		description: 'ChatGPT, or any app that adds a connector by its address.',
		channel: 'connector',
	},
	{
		key: 'agent',
		title: 'A coding agent',
		description: 'Codex, Cursor, Gemini CLI and the rest.',
		channel: 'skill',
		// "Use a key instead" names the key after the agent.
		keyName: 'My coding agent',
	},
];

/**
 * The one way that still makes a key, under "Other ways": for developers,
 * their scripts and imports. No assistant needs it to connect.
 */
export const KEY_ROUTE = {
	key: 'key',
	title: 'Use an API key',
	description: 'For developers, scripts and imports. You keep the key safe yourself.',
	keyName: 'My API key',
};

/** What every route shares, said once under the three. */
export const ROUTES_NOTE = 'Each one signs in on a keepr page. No key to copy, nothing to download.';

/** Access, one line each. `write` implies `read` on the server. */
const ACCESS_OPTIONS = [
	{ value: 'read', label: 'Read only', note: 'It can look things up.' },
	{ value: 'write', label: 'Read and write', note: 'It can also add and update items.' },
];

/** The connection check: every five seconds, for five minutes. */
export const POLL_MS = 5000;
export const MAX_POLLS = 60;

/**
 * A connection made a moment before the route was chosen still counts:
 * the server's clock and this one differ (KPR-199's allowance).
 */
export const CLOCK_SKEW_MS = 60 * 1000;

// Can change cards and Can delete records both start OFF: the tool a person
// connects here is almost always "let my assistant log things", and neither
// redesigning the data nor removing it is part of that.
const EMPTY_FORM = { name: '', scope: 'write', cards: false, canDelete: false, collectionMode: 'all', collectionIds: [], expiresIn: '90d' };

const PICK_FIRST = 'Pick where you use your assistant first.';
const KEY_FIRST = 'Create the key first.';

/** A block of text with one Copy button beside it — a command, a sentence, an address. */
function CopyBlock({ text, label, onCopied }) {
	return (
		<div className="connect-copy">
			<pre className="connect-copy__text"><code>{text}</code></pre>
			<Button icon={<CopyOutlined />} onClick={() => onCopied(text, label)} aria-label={`Copy ${label.toLowerCase()}`}>Copy</Button>
		</div>
	);
}

/** A step: its numbered header, then either its content or why it waits. */
function Step({ number, title, locked = null, children }) {
	return (
		<section className={`connect-step${locked ? ' connect-step--locked' : ''}`} aria-labelledby={`connect-step-${number}`}>
			<GroupHeader label={`${number} · ${title}`} id={`connect-step-${number}`} role="heading" aria-level={2} />
			{locked ? <p className="connect-step__locked">{locked}</p> : children}
		</section>
	);
}

/** One place an assistant runs, inside a route's step 2. */
function Surface({ title, children }) {
	return (
		<div className="connect-surface">
			<h3 className="connect-surface__title">{title}</h3>
			{children}
		</div>
	);
}

/** A fresh connection check, started when a sign-in route is chosen. */
const startWatch = () => ({ since: Date.now() - CLOCK_SKEW_MS, state: 'waiting', name: null });

/**
 * /account/connect — Connect an assistant. One question (where do you use
 * it?), three routes that each sign in on keepr's consent page, and "Other
 * ways" for a developer's key and for moving from an older setup. Then only
 * that route: where to add keepr, the Allow on keepr's page, a connection
 * check that turns green when the new connection appears under Connected
 * assistants, and three prompts to try. The key route keeps the inline key
 * form, its one-time reveal and its first-use check. Nothing on this page
 * downloads anything (ruling d12).
 */
export default function ConnectAssistant() {
	const { message } = App.useApp();

	const [routeKey, setRouteKey] = useState(null);
	const [othersOpen, setOthersOpen] = useState(false);
	const { collections } = useCollections();
	const [form, setForm] = useState(EMPTY_FORM);
	const [creating, setCreating] = useState(false);
	// The one-time reveal: { key, token }, from the create response.
	const [created, setCreated] = useState(null);
	// The key's check: 'idle' | 'waiting' | 'connected' | 'timeout'.
	const [connection, setConnection] = useState('idle');
	// The sign-in check: { since, state: 'waiting' | 'connected' | 'timeout', name }.
	const [signIn, setSignIn] = useState(null);

	const route = useMemo(() => (routeKey === KEY_ROUTE.key ? KEY_ROUTE : ROUTES.find(candidate => candidate.key === routeKey) || null), [routeKey]);
	const keyRoute = route === KEY_ROUTE;
	const signInRoute = Boolean(route && !keyRoute);


	// The key's check: polls the key until it is first used. Stops on
	// unmount, on success, and after five minutes — then Retry starts it again.
	useEffect(() => {
		if (connection !== 'waiting' || !created?.key?.id) return undefined;
		let active = true;
		let attempts = 0;
		const keyId = normalizeId(created.key.id);
		const tick = async () => {
			attempts += 1;
			try {
				const rows = await listApiKeys();
				if (!active) return;
				const row = rows.find(candidate => normalizeId(candidate.id) === keyId);
				if (row?.lastUsedAt) { setConnection('connected'); return; }
			} catch {
				// A failed poll is not a failed connection; the next tick asks again.
			}
			if (active && attempts >= MAX_POLLS) setConnection('timeout');
		};
		const timer = setInterval(tick, POLL_MS);
		return () => { active = false; clearInterval(timer); };
	}, [connection, created]);

	// The sign-in check (KPR-199's grant watch): a connection made since the
	// route was chosen — less a minute for clocks that differ — and not
	// disconnected turns it green. An older connection never counts.
	const watching = signInRoute && signIn?.state === 'waiting';
	const since = signIn?.since;
	useEffect(() => {
		if (!watching) return undefined;
		let active = true;
		let attempts = 0;
		const tick = async () => {
			attempts += 1;
			try {
				const grants = await listGrants();
				if (!active) return;
				const fresh = grants.find(grant => !grant?.revokedAt && Date.parse(grant?.createdAt) >= since);
				if (fresh) {
					setSignIn(prev => ({ ...prev, state: 'connected', name: fresh.name || fresh.clientName || 'Your assistant' }));
					return;
				}
			} catch {
				// A failed poll is not a failed connection; the next tick asks again.
			}
			if (active && attempts >= MAX_POLLS) setSignIn(prev => ({ ...prev, state: 'timeout' }));
		};
		const timer = setInterval(tick, POLL_MS);
		return () => { active = false; clearInterval(timer); };
	}, [watching, since]);

	// Alphabetical, each sub-collection indented beneath its parent.
	const collectionOptions = useMemo(() => nestSubCollections(collections
		.filter(collection => normalizeId(collection?._id))
		.sort((a, b) => (a?.name || '').localeCompare(b?.name || '')))
		.map(({ collection, depth }) => ({
			value: normalizeId(collection._id),
			label: collection.name || '',
			className: nestedOptionClass(depth),
		})), [collections]);

	const chooseRoute = (candidate, { keyName } = {}) => {
		if (!candidate || created) return;
		setRouteKey(candidate.key);
		setForm({ ...EMPTY_FORM, name: keyName || candidate.keyName || '' });
		if (candidate === KEY_ROUTE) {
			setOthersOpen(true);
			setSignIn(null);
		} else {
			setSignIn(startWatch());
		}
	};

	const startOver = () => {
		setRouteKey(null);
		setForm(EMPTY_FORM);
		setCreated(null);
		setConnection('idle');
		setSignIn(null);
	};

	const setField = (name, value) => setForm(prev => ({ ...prev, [name]: value }));

	const nameValid = form.name.trim().length > 0 && form.name.trim().length <= 80;
	const collectionsValid = form.collectionMode === 'all' || form.collectionIds.length > 0;
	const canSubmit = nameValid && collectionsValid && !creating;
	const deleteAllowed = deleteScopeAllowed(form.scope, form.cards);

	const handleCreate = useCallback(async () => {
		if (!canSubmit) return;
		setCreating(true);
		try {
			const result = await createApiKey({
				name: form.name,
				scopes: form.scope,
				cards: form.cards,
				canDelete: form.canDelete && deleteAllowed,
				collectionIds: form.collectionMode === 'some' ? form.collectionIds : undefined,
				expiresIn: form.expiresIn || undefined,
			});
			setCreated(result);
			setConnection('waiting');
		} catch (error) {
			message.error(apiErrorMessage(error, 'Could not create the API key.'));
		} finally {
			setCreating(false);
		}
	}, [canSubmit, form, deleteAllowed, message]);

	const handleCopy = useCallback(async (text, label) => {
		if (await copyTextToClipboard(text)) message.success(`${label} copied`);
		else message.error(`Unable to copy the ${label.toLowerCase()}`);
	}, [message]);

	// The prompts name what the person just allowed, when they narrowed the key.
	const firstPicked = keyRoute && form.collectionMode === 'some' && form.collectionIds.length
		? collectionOptions.find(option => option.value === normalizeId(form.collectionIds[0]))?.label
		: null;
	const collectionName = firstPicked || collectionOptions[0]?.label || 'Books';
	const prompts = [
		`What's in my ${collectionName} collection?`,
		`Add this to my ${collectionName} collection: `,
		`Add an element called "notes" to a card in my ${collectionName} collection`,
	];

	const updatesLink = (channel) => (
		<p className="connect-updates">
			<Link to={clientUpdateHref(channel)}>How updates work</Link>
		</p>
	);

	const addressBlock = <CopyBlock text={MCP_CONNECTOR_URL} label="Connector address" onCopied={handleCopy} />;

	// ── Step 2, per route ──────────────────────────────────────────────

	// Claude chat by its address: the connector, or the plugin that carries
	// it. Claude's own menu names (claude.com/docs, checked 2026-10-07).
	const claudeByAddress = (
		<>
			{addressBlock}
			<ol className="connect-list">
				<li>In Claude, open <strong>Customize</strong> → <strong>Connectors</strong> → <strong>Add custom connector</strong>, paste the address and press <strong>Add</strong>.</li>
				<li>Press <strong>Connect</strong>.</li>
			</ol>
			<p className="mt-3 mb-2">
				Or add the keepr plugin, which also teaches Claude how to work in keepr: <strong>Customize</strong> → <strong>Plugins</strong> → <strong>Add</strong> → <strong>Add marketplace</strong>, enter <code>{PLUGIN_MARKETPLACE}</code> and add <strong>keepr</strong>. Then open its <strong>Connectors</strong> tab and press <strong>Connect</strong>.
			</p>
			<p className="form-panel__hint mb-0">
				Add it once, on the web or in the desktop app: Claude on your phone has it too. On a Team or Enterprise plan an Owner adds it for everyone, and you press <strong>Connect</strong>.
			</p>
		</>
	);

	const claudeChat = CLAUDE_DIRECTORY_URL ? (
		<>
			<Button type="primary" href={CLAUDE_DIRECTORY_URL} {...EXTERNAL_LINK_PROPS}>Add keepr to Claude</Button>
			<p className="mt-2 mb-0">It opens keepr in Claude's directory. Add it there, then press <strong>Connect</strong>.</p>
			<Collapse
				ghost
				className="connect-fallback"
				items={[{
					key: 'address',
					label: 'No directory on your plan? Add keepr by its address',
					children: claudeByAddress,
				}]}
			/>
		</>
	) : claudeByAddress;

	const claudeSetup = (
		<div className="form-panel">
			<Surface title="Claude chat — web, desktop and phone">
				{claudeChat}
			</Surface>
			<Surface title="Claude Code">
				<p className="mb-2">In a terminal, run these two commands. If you added the keepr plugin in Claude and Claude Code is signed in to the same account, it is there already: skip them.</p>
				<CopyBlock text={CLAUDE_CODE_COMMANDS.join('\n')} label="Commands" onCopied={handleCopy} />
				<p className="mt-3 mb-2">
					Then in Claude Code type <code>/mcp</code>, choose keepr, choose <strong>Authenticate</strong>, and press <strong>Allow</strong> on the keepr page that opens. The first time it attaches a file from your computer, keepr asks once more.
				</p>
				<p className="form-panel__hint mb-0">
					<strong>Keep it up to date:</strong> type <code>/plugin</code>, open <strong>Marketplaces</strong>, choose <strong>keepr-agent</strong>, then <strong>Enable auto-update</strong>.
				</p>
				{updatesLink('plugin')}
			</Surface>
			<Surface title="Cowork">
				<p className="mb-0">Add the keepr plugin in Claude, as above. Cowork loads it in your next task, with keepr's file tools for files on your computer.</p>
			</Surface>
		</div>
	);

	const appsSetup = (
		<div className="form-panel">
			{addressBlock}
			<ul className="connect-list">
				<li><strong>ChatGPT</strong> — add keepr as an app by this address, in ChatGPT's settings. Some plans ask you to turn on developer mode there first. Sign in when it asks.</li>
				<li><strong>Any other app</strong> — add the address where the app adds a connector (some call it an MCP server), and choose to sign in if it asks how.</li>
			</ul>
		</div>
	);

	const agentSetup = (
		<div className="form-panel">
			<Surface title="If your agent takes a connector">
				<p className="mb-2">Codex, Cursor and Gemini CLI do. Add this address where your agent adds MCP servers, then sign in when it asks.</p>
				{addressBlock}
			</Surface>
			<Surface title="Otherwise, give it the keepr skill">
				<p className="mb-2">Paste this sentence to your agent.</p>
				<CopyBlock text={SKILL_INSTALL_SENTENCE} label="Sentence" onCopied={handleCopy} />
				<p className="mt-3 mb-0">
					Then ask it to sign in to keepr. It runs <code>{LOGIN_COMMAND}</code>, which opens keepr in your browser. If it cannot open a browser where it runs, run that command yourself on the computer you are at.
				</p>
				{updatesLink('skill')}
			</Surface>
			<p className="form-panel__hint mb-0">
				Need a key for it?{' '}
				<Button type="link" size="small" className="connect-inline-link" onClick={() => chooseRoute(KEY_ROUTE, { keyName: route?.keyName })}>Use a key instead</Button>
			</p>
		</div>
	);

	// ── The key route ──────────────────────────────────────────────────

	const keyForm = (
		<div className="form-panel connect-key">
			<div className="connect-field">
				<label className="connect-field__label" htmlFor="connect-key-name">Name</label>
				<Input
					id="connect-key-name"
					maxLength={80}
					value={form.name}
					onChange={event => setField('name', event.target.value)}
				/>
				<p className="form-panel__hint">How this key shows in your API keys list.</p>
			</div>

			<div className="connect-field">
				<span className="connect-field__label" id="connect-key-collections-label">Collections</span>
				<Radio.Group
					aria-labelledby="connect-key-collections-label"
					value={form.collectionMode}
					onChange={event => setField('collectionMode', event.target.value)}
				>
					<Radio value="all">All collections I can access</Radio>
					<Radio value="some">Only these collections</Radio>
				</Radio.Group>
				{form.collectionMode === 'some' ? (
					<Select
						mode="multiple"
						id="connect-key-collections"
						aria-label="Collections"
						className="connect-field__select mt-2"
						placeholder="Pick collections"
						value={form.collectionIds}
						options={collectionOptions}
						optionFilterProp="label"
						onChange={value => setField('collectionIds', value)}
					/>
				) : null}
			</div>

			<div className="connect-field">
				<span className="connect-field__label" id="connect-key-access-label">Access</span>
				<Radio.Group
					aria-labelledby="connect-key-access-label"
					className="connect-access"
					value={form.scope}
					onChange={event => setField('scope', event.target.value)}
				>
					{ACCESS_OPTIONS.map(option => (
						<Radio key={option.value} value={option.value} className="connect-access__option">
							<span className="connect-access__label">{option.label}</span>
							<span className="connect-access__note">{option.note}</span>
						</Radio>
					))}
				</Radio.Group>
				<ToggleRow
					id="connect-key-cards"
					first
					label={CARDS_SCOPE_LABEL}
					description={CARDS_SCOPE_NOTE}
					checked={form.cards}
					onChange={value => setField('cards', value)}
				/>
				<ToggleRow
					id="connect-key-delete"
					label={DELETE_SCOPE_LABEL}
					description={deleteAllowed ? DELETE_SCOPE_NOTE : DELETE_SCOPE_NEEDS_WRITE}
					checked={form.canDelete && deleteAllowed}
					disabled={!deleteAllowed}
					onChange={value => setField('canDelete', value)}
				/>
			</div>

			<div className="connect-field">
				<label className="connect-field__label" htmlFor="connect-key-expiry">Expires</label>
				<Select
					id="connect-key-expiry"
					className="connect-field__select"
					value={form.expiresIn}
					options={EXPIRY_OPTIONS}
					onChange={value => setField('expiresIn', value)}
				/>
			</div>

			<div className="connect-field connect-field--actions">
				<Button type="primary" onClick={handleCreate} disabled={!canSubmit} loading={creating}>Create key</Button>
				{!nameValid ? <Text type="secondary">Give the key a name.</Text>
					: !collectionsValid ? <Text type="secondary">Pick at least one collection.</Text> : null}
			</div>
		</div>
	);

	const reveal = created ? (
		<div className="form-panel connect-reveal">
			<Alert type="warning" showIcon message={ONE_TIME_TOKEN_NOTE} />
			<code className="api-keys__token" data-testid="connect-key-token">{created.token}</code>
			<Button icon={<CopyOutlined />} type="primary" onClick={() => handleCopy(created.token, 'Key')}>Copy key</Button>
			<p className="connect-reveal__rule">Paste it only where step 3 says. Never into a chat.</p>
		</div>
	) : null;

	const keySetup = (
		<div className="form-panel">
			<ul className="connect-list mt-0">
				<li className="connect-list__block">
					<strong>keepr.py</strong>, the script in the keepr skill — run this yourself, in your own terminal, and paste the key at its prompt. It hides what you type and refuses anything but a person at the keyboard.
					<CopyBlock text={LOGIN_KEY_COMMAND} label="Command" onCopied={handleCopy} />
				</li>
				<li>
					<strong>Your own code or an import</strong> — put it in that tool's own setting.{' '}
					<Link to="/docs/guides/api-and-integrations/index">API and integrations</Link> shows how keepr reads it.
				</li>
			</ul>
		</div>
	);

	// ── Step 4 ─────────────────────────────────────────────────────────

	let keyCheck = null;
	if (connection === 'connected') {
		keyCheck = <Alert type="success" showIcon message="Connected — your assistant reached keepr just now." />;
	} else if (connection === 'timeout') {
		keyCheck = (
			<Alert
				type="info"
				showIcon
				message="Nothing has called in yet."
				description="Finish step 3, then use the key once."
				action={<Button size="small" onClick={() => setConnection('waiting')}>Retry</Button>}
			/>
		);
	} else if (connection === 'waiting') {
		keyCheck = <Alert type="info" showIcon message="Waiting for your assistant to call in…" description="This turns green the first time something reaches keepr with the key." />;
	}

	let signInCheck = null;
	if (signIn?.state === 'connected') {
		signInCheck = <Alert type="success" showIcon message={`Connected — ${signIn.name} is under Connected assistants.`} />;
	} else if (signIn?.state === 'timeout') {
		signInCheck = (
			<Alert
				type="info"
				showIcon
				message="No new connection yet."
				description="Finish step 2, and press Allow on the keepr page your assistant opens."
				action={<Button size="small" onClick={() => setSignIn(prev => ({ ...prev, state: 'waiting' }))}>Retry</Button>}
			/>
		);
	} else if (signIn?.state === 'waiting') {
		signInCheck = <Alert type="info" showIcon message="Waiting for you to press Allow…" description="This turns green when the new connection appears under Connected assistants." />;
	}

	// ── Step 1 ─────────────────────────────────────────────────────────

	// The three routes are one radio group. The key route, under Other ways,
	// is a toggle button beside a link, so the group holds only radios.
	const renderRoute = (candidate) => {
		const selected = candidate.key === routeKey;
		const choice = candidate === KEY_ROUTE ? { 'aria-pressed': selected } : { role: 'radio', 'aria-checked': selected };
		return (
			<button
				key={candidate.key}
				type="button"
				{...choice}
				aria-disabled={created && !selected ? true : undefined}
				className={`connect-path${selected ? ' connect-path--selected' : ''}`}
				onClick={() => chooseRoute(candidate)}
			>
				<span className="connect-path__title">
					{candidate.title}
					{candidate.recommended ? <Tag className="connect-path__tag">Recommended</Tag> : null}
				</span>
				<span className="connect-path__description">{candidate.description}</span>
			</button>
		);
	};

	const otherWays = (
		<div className="connect-others">
			{renderRoute(KEY_ROUTE)}
			<Link className="connect-others__link" to={UPDATE_GUIDE_PATH}>
				<span className="connect-path__title">Moving from an older setup</span>
				<span className="connect-path__description">The desktop extension, an uploaded skill, or a key in Claude Code: how to move, and what to remove.</span>
			</Link>
		</div>
	);

	let setup = null;
	if (route?.key === 'claude') setup = claudeSetup;
	else if (route?.key === 'apps') setup = appsSetup;
	else if (route?.key === 'agent') setup = agentSetup;

	const step2Title = keyRoute ? 'Create a key' : (route?.key === 'agent' ? 'Connect your agent' : 'Add keepr');
	const step3Locked = !route ? PICK_FIRST : (keyRoute && !created ? KEY_FIRST : null);
	const step4Locked = !route ? PICK_FIRST : (keyRoute && !created ? KEY_FIRST : null);

	return (
		<>
			<ContextBar
				breadcrumb={[{ label: 'Account', to: '/account' }]}
				title="Connect an assistant"
				below="Let Claude, ChatGPT or a coding agent read, add and change items for you."
			/>

			<div className="settings-column connect-assistant">
				<Step number={1} title="Where do you use your assistant?">
					<div className="connect-choices">
						<div className="connect-paths" role="radiogroup" aria-label="Where do you use your assistant?">
							{ROUTES.map(renderRoute)}
						</div>
						<p className="connect-why">{ROUTES_NOTE}</p>
						<Collapse
							ghost
							className="connect-other-ways"
							activeKey={othersOpen ? ['others'] : []}
							onChange={keys => setOthersOpen((Array.isArray(keys) ? keys : [keys]).includes('others'))}
							items={[{ key: 'others', label: 'Other ways', children: otherWays }]}
						/>
					</div>
					{created ? (
						<p className="form-panel__hint mt-2">
							A key was made here. <Button type="link" size="small" className="connect-inline-link" onClick={startOver}>Start over</Button> to pick another way.
						</p>
					) : null}
				</Step>

				<Step number={2} title={route ? step2Title : 'Add keepr'} locked={!route ? PICK_FIRST : null}>
					{keyRoute ? (created ? reveal : keyForm) : setup}
				</Step>

				<Step number={3} title={keyRoute ? 'Use the key' : "Allow it on keepr's page"} locked={step3Locked}>
					{keyRoute ? keySetup : (
						<div className="form-panel">
							<p className="mb-2">
								The first time, your assistant opens a keepr page that asks what it may reach. Choose the collections and the access, then press <strong>Allow</strong>. Leave <strong>{DELETE_SCOPE_LABEL}</strong> off unless it is meant to remove things.
							</p>
							<p className="form-panel__hint mb-0">You can disconnect it any time under <strong>Connected assistants</strong> on your account page.</p>
						</div>
					)}
				</Step>

				<Step number={4} title="Check the connection" locked={step4Locked}>
					{keyRoute ? keyCheck : signInCheck}
				</Step>

				<Step number={5} title="Try it" locked={step3Locked}>
					<div className="connect-prompts">
						{prompts.map(prompt => (
							<Button key={prompt} className="connect-prompt" icon={<CopyOutlined />} onClick={() => handleCopy(prompt, 'Prompt')}>
								{prompt}
							</Button>
						))}
					</div>
				</Step>
			</div>
		</>
	);
}
